import React from 'react';
import { NavLink } from 'react-router-dom';

/**
 * Top tab navigation that sits above the hero on every admin page.
 *
 * Single source of truth for the tab list — both AdminUsers and
 * AdminConfig render this component so the order, labels, icons and
 * active styling stay consistent. To add a new tab, add an entry to
 * `TABS` and create the matching <Route> in App.tsx.
 *
 * NavLink resolves the `active` class automatically based on URL match,
 * which means refreshing on a deep-linked tab still shows the right
 * highlight without any extra wiring.
 */

interface AdminTab {
  to: string;
  label: string;
  icon: React.ReactNode;
}

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SystemIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const ClassificationsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const TABS: readonly AdminTab[] = [
  { to: '/admin/users', label: 'Utilizatori', icon: <UsersIcon /> },
  { to: '/admin/classifications', label: 'Clasificări', icon: <ClassificationsIcon /> },
  { to: '/admin/system', label: 'Sistem', icon: <SystemIcon /> },
];

const AdminTabsNav: React.FC = () => (
  <nav className="tabs-bar admin-tabs-nav" aria-label="Secțiuni administrare">
    {TABS.map((tab) => (
      <NavLink
        key={tab.to}
        to={tab.to}
        className={({ isActive }) =>
          `tabs-bar__tab admin-tab${isActive ? ' is-active admin-tab--active' : ''}`
        }
      >
        <span className="admin-tab__icon">{tab.icon}</span>
        <span className="admin-tab__label">{tab.label}</span>
      </NavLink>
    ))}
  </nav>
);

export default AdminTabsNav;
